/**
 * Document RPA trigger modal with per-document selection and linked sibling orders.
 * @module triggerRpaWithDocListModal
 */
import moment from "moment";
import { useEffect, useState } from "react";

import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { getViewerDocumentTitleByDocType } from "../../../constants/statusToDocMap/statusToDocMap";
import { JCodeDocuments } from "../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../enums/modalId";
import WarningIcon from "../../../svg/warningIcon";
import CustomCheckBox from "../../customCheckBox/customCheckBox";
import { useLinkedOrdersModalView } from "../../expandableTableForOrderAuth/hooks/useLinkedOrdersModalView";
import RenderSiblingOrderContentBody from "../updateCigNoteConfirmationDialog/renderSiblingOrderContentBody";

const DEFAULT_TITLE = "Trigger RPA";

/** Props for {@link TriggerRpaWithDocListModal}. */
interface TriggerRpaWithDocListModalProps {
  /** Closes the modal. */
  onClose: () => void;
  /** Cancels without triggering RPA. */
  onCancel: () => void;
  /** Fires with selected documents and optional linked order ids when confirmed. */
  onTriggerRpa: (
    selectedDocuments: JCodeDocuments[],
    selectedOrderIds?: string[],
  ) => void;
  /** When true, disables save and shows a loading label on the primary button. */
  loading: boolean;
  /** Medical PA order identifier. */
  orderId: string;
  /** Uploadable documents for the current order. */
  docList: JCodeDocuments[];
}

/**
 * Lets the user pick documents to upload via RPA and shows linked sibling
 * orders below when already filtered in context. Sibling checkboxes stay
 * interactive for multi-select.
 *
 * @param props - Close/cancel handlers, trigger callback, loading flag, and doc list
 * @returns Document-list trigger RPA modal
 */
const TriggerRpaWithDocListModal = (props: TriggerRpaWithDocListModalProps) => {
  const [allDocs, setAllDocs] = useState<JCodeDocuments[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<JCodeDocuments[]>(
    [],
  );
  const {
    expandableTableData,
    siblingOrdersLoading,
    siblingOrdersError,
    hasLinkedOrders,
    selectedSiblingOrderIds,
  } = useLinkedOrdersModalView();

  useEffect(() => {
    setAllDocs(props.docList);
  }, [props.docList]);

  const handleTriggerRpa = () => {
    props.onTriggerRpa(
      selectedDocuments,
      expandableTableData.length > 0 && selectedSiblingOrderIds.length > 0
        ? selectedSiblingOrderIds
        : undefined,
    );
  };

  const requiresDocumentSelection =
    !hasLinkedOrders &&
    (allDocs.length === 0 || selectedDocuments.length === 0);

  return (
    <Modal
      dialogId={ModalId.TRIGGER_RPA_WITH_DOC_LIST_MODAL}
      title={DEFAULT_TITLE}
      onSave={handleTriggerRpa}
      onClose={props.onClose}
      onCancel={props.onCancel}
      saveButtonText={props.loading ? "Triggering..." : "Trigger RPA"}
      cancelText="Cancel"
      disableSave={props.loading || requiresDocumentSelection}
      maxHeightPercentage={hasLinkedOrders ? 80 : undefined}
    >
      <div className="trigger-rpa-modal-container flex flex-col gap-4">
        <div className="documents-section">
          {allDocs.length === 0 ? (
            <NoDocumentsToUpload />
          ) : (
            <>
              <div className="mb-3 text-small text-primaryGray-1">
                Select documents to be uploaded
              </div>
              <div className="documents-list flex flex-col gap-3">
                {allDocs.map((doc, index) => (
                  <div
                    className="custom-checkbox-with-timestamp flex items-center justify-between gap-1"
                    key={
                      doc.doc_path +
                      "_" +
                      doc.doc_type +
                      "_" +
                      doc.doc_title +
                      "_" +
                      index
                    }
                  >
                    <CustomCheckBox
                      id={
                        doc.doc_path +
                        "_" +
                        doc.doc_type +
                        "_" +
                        doc.doc_title +
                        "_" +
                        index
                      }
                      label={
                        doc.doc_title +
                        " (" +
                        getViewerDocumentTitleByDocType(doc.doc_type ?? "") +
                        ")"
                      }
                      className="!mt-[3px] !text-small"
                      isChecked={selectedDocuments.includes(doc)}
                      onCheckBoxValueChange={(value) => {
                        if (value.value) {
                          setSelectedDocuments([...selectedDocuments, doc]);
                        } else {
                          setSelectedDocuments((prev) =>
                            prev.filter((d) => d.doc_path !== doc.doc_path),
                          );
                        }
                      }}
                    />
                    <div className="mt-[6px] text-xs italic text-primaryGray-5">
                      Last Uploaded on{" "}
                      {moment(doc.doc_upload_date).format("MM/DD/YYYY")}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-md bg-secondaryYellow-11 p-3">
                <p className="text-sm italic text-primaryGray-5">
                  If NAR is based on auth grid, skip PDF
                </p>
              </div>
            </>
          )}
        </div>

        <RenderSiblingOrderContentBody
          expandedContentType="table"
          expandableTableData={expandableTableData}
          siblingOrdersLoading={siblingOrdersLoading}
          siblingOrdersError={siblingOrdersError}
          descriptionText="Trigger for following orders as well?"
        />
      </div>
    </Modal>
  );
};

export default TriggerRpaWithDocListModal;

/**
 * Empty state when no uploadable documents are available for the order.
 *
 * @returns Warning copy prompting the user to upload documents first
 */
const NoDocumentsToUpload = () => {
  return (
    <div className="no-documents-to-upload flex flex-col items-center justify-center gap-2">
      <WarningIcon height="24" width="24" />
      <div className="text-sm italic text-primaryGray-5">
        No relevant documents found to be uploaded.
      </div>
      <div className="text-sm italic text-primaryGray-5">
        Please upload the documents first.
      </div>
    </div>
  );
};
